import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import PersonAddIcon from '@atlaskit/icon/core/person-add';
import Pressable from '@atlaskit/primitives/pressable';

import type { PresenceContentType, PresenceContentMode } from '../presenceTypes';

type AddPeopleProps = {
	contentType: PresenceContentType;
	contentMode: PresenceContentMode;
	toggleEmptyPresenceDialog: () => void;
};

const i18n = defineMessages({
	addPeopleLabel: {
		id: 'team-presence.avatar-button.add-people-label',
		defaultMessage: 'Add people to this {contentType}',
		description: 'Accessible label for presence avatar add people button',
	},
	contentTypeLabel: {
		id: 'team-presence.avatar-button.content-type-label',
		defaultMessage:
			'{contentType, select, page {page} blogpost {blog post} whiteboard {whiteboard} livepage {live doc} database {database} other {content}}',
		description: 'Label for content type in presence avatar add people button',
	},
});

const iconButtonStyles = xcss({
	marginLeft: 'space.negative.100',
});

const pressableStyles = xcss({
	height: '28px',
	width: '28px',
	borderRadius: '50%',
	borderWidth: '2px',
	borderStyle: 'solid',
	borderColor: 'color.border',
	color: 'color.text.subtle',
	fontWeight: 'font.weight.medium',
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.050',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	':hover': {
		textDecoration: 'underline',
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		color: 'color.link.pressed',
		borderColor: 'color.border.selected',
		backgroundColor: 'color.background.selected',
	},
});
export const AddPeopleButton = ({
	contentType,
	contentMode,
	toggleEmptyPresenceDialog,
}: AddPeopleProps) => {
	const { formatMessage } = useIntl();

	// TODO ENGAGEMENT-5503: update with suggested dialog
	const getPersonAddButtonOnClickHandler = () => {
		return toggleEmptyPresenceDialog;
	};

	const contentTypeLabel = formatMessage(i18n.contentTypeLabel, {
		contentType: contentType === 'page' && contentMode === 'live' ? 'livepage' : contentType,
	});

	const addPeopleButtonLabel = formatMessage(i18n.addPeopleLabel, {
		contentType: contentTypeLabel,
	});

	return (
		<Box xcss={iconButtonStyles}>
			<Tooltip content={addPeopleButtonLabel}>
				{(tooltipProps) => (
					<Pressable
						{...tooltipProps}
						xcss={pressableStyles}
						aria-label={addPeopleButtonLabel}
						testId={`team-presence-avatar-add-people-button`}
						onClick={getPersonAddButtonOnClickHandler()}
					>
						<PersonAddIcon label="" />
					</Pressable>
				)}
			</Tooltip>
		</Box>
	);
};
